import { ToastrService } from 'ngx-toastr';
import {Action, createFeatureSelector, createSelector, select, Store} from '@ngrx/store';
import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, combineLatest, exhaustMap, filter, map, mapTo, mergeMap, switchMap, take, tap} from 'rxjs/operators';
import {forkJoin, Observable, of} from 'rxjs';
import * as _ from 'lodash';

import {resolveIcon, toQueryString} from '../../core/services/utils';
// Payload Types
import {BaseStateV2} from '../../core/base.state';
import {Service} from './service';

import {getProfile as userProfile} from '../../core/auth/store';
import {CoreStore} from '../../core/core.store';
import * as UILoaderStore from '../../shared/loader/store';
import * as UserStore from '../user/store';

export const FEATURE_NAME       = 'FOLDER';
export const RESOURCE: string   = 'folders';

const FeatureActionTypes = {
    MergeChildren: `[${FEATURE_NAME}]` + " MergeChildren",
    LoadUserFolders: `[${FEATURE_NAME}]` + " LoadUserFolders",
    LoadUserFoldersSuccess: `[${FEATURE_NAME}]` + " LoadUserFoldersSuccess",
    UnLoadUserFolders: `[${FEATURE_NAME}]` + " UnLoadUserFolders",
};

import {State as rootState} from '../../../reducers';

class IFeatureActions extends CoreStore.Actions {

    MergeChildren: Action | any;
    LoadUserFolders: Action | any;
    LoadUserFoldersSuccess: Action | any;
    UnLoadUserFolders: Action | any;

    constructor(featureName: string){
        super(featureName);
        this.buildFeatureActions();
    }

    buildFeatureActions(){
        this._types = _.merge(this._types, FeatureActionTypes);
        this.MergeChildren = class implements Action {
            public readonly type: any = FeatureActionTypes.MergeChildren;
            constructor(public payload: any) {}
        };
        this.LoadUserFolders = class implements Action {
            public readonly type: any = FeatureActionTypes.LoadUserFolders;
            constructor(public payload: any) {}
        };
        this.LoadUserFoldersSuccess = class implements Action {
            public readonly type: any = FeatureActionTypes.LoadUserFoldersSuccess;
            constructor(public payload: any) {}
        };
        this.UnLoadUserFolders = class implements Action {
            public readonly type: any = FeatureActionTypes.UnLoadUserFolders;
            constructor(public payload: any) {}
        };
    }

}

export const FeatureActions: IFeatureActions     = new IFeatureActions(FEATURE_NAME);

export const initialState: IState = {
    ...CoreStore.initialState,
    rootFolder: {},
    userFoldersLoaded: false
};

export function reducer(state = initialState, action: any): any {
    switch (action.type) {

        case FeatureActions.Types.MergeChildren: {
            return {
                ...state,
                data: {
                    ...state.data,
                    children: _.flatten([...state.data.children, ...action.payload])
                }
            }
        }

        case FeatureActionTypes.UnLoadUserFolders: {
            return {
                ...state,
                data: {
                    ...state.data,
                    children: _.filter(state.data.children, (f) => !f.anotherUserFolder)
                }
            }
        }

        default: {
            return CoreStore.reducer(state, action, FeatureActions)
        }

    }
}

export interface IState extends CoreStore.State {
    rootFolder: any;
    userFoldersLoaded: boolean;
}

const selectState = createFeatureSelector<IState>(FEATURE_NAME);

// Effects

@Injectable()
export class Effects {

    private _coreEffectsOptions: CoreStore.EffectsOptions = {
        FEATURE_NAME: FEATURE_NAME,
        Actions: FeatureActions,
        featureService: this._svc,
        actions$: this.actions$,
        initialState: initialState
    };

    @Effect()
    Load$ = CoreStore.Effects.Load(this._coreEffectsOptions,
        (obj) => 'folders?' + toQueryString(obj),
        (obj, results) => {
            return _.merge(results, { anotherUserFolder: obj.anotherUserFolder })
        });

    @Effect({dispatch: true})
    LoadUserFolders$ = this.actions$.pipe(
        ofType(FeatureActionTypes.LoadUserFolders),
        map((action: any) => action.payload),
        combineLatest(
            this.store.select(selectors.rootFolder).pipe(take(2))
        ),
        exhaustMap((obj) =>
            this._svc.doLoad('folders/root?loadAnotherUsersOnly=true')
                .pipe(
                    map((data) => {
                        return _.map(data, userFolder =>
                            _.merge(userFolder, {
                                name: 'Home Folder ' + userFolder.owner.username,
                                anotherUserFolder: true,
                                viewPath: obj[1].path + 'Home Folder ' + userFolder.owner.username,
                                parent: obj[1],
                                isRoot: false
                            })
                        )
                    }),
                    map(processed => new FeatureActions.LoadUserFoldersSuccess(processed)),
                    catchError(error => of(new this._coreEffectsOptions.Actions.LoadFailure(error)))
                ),
        ),
    );

    @Effect()
    LoadUserFoldersSuccess$ = this.actions$.pipe(
        ofType(FeatureActionTypes.LoadUserFoldersSuccess),
        map((action: any) => action.payload),
        mergeMap((results) =>
            [
                new FeatureActions.MergeChildren(results),
                new FeatureActions.Set({ userFoldersLoaded: true })
            ]
        )
    );

    @Effect()
    Create$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.Create),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
        this._coreEffectsOptions.featureService.doAdd('folders?' + toQueryString({path: obj.path}))
            .pipe(
                map(data => {
                    this.toastrService.success('', 'The folder is successfully created.', {
                        timeOut: 3000
                    });
                    if(obj.isMyFilesSection) {
                        return (new this._coreEffectsOptions.Actions.Load({
                            id: data.parent.id,
                            loadMyFiles: true
                        }));
                    }
                    else {
                        return (new this._coreEffectsOptions.Actions.CreateSuccess(data));
                    }
                }),
                catchError(error => {
                    this.toastrService.error(
                        '<b>If the problem persists, please contact an administrator.</b>',
                        'An error occured while creating the folder. Please try again.',
                        {
                            closeButton: true,
                            disableTimeOut: true,
                            enableHtml: true
                        }
                    );
                    return (of(new this._coreEffectsOptions.Actions.CreateFailure(error)));
                })
            ),
        )
    );

    @Effect()
    Delete$ = CoreStore.Effects.Delete( this._coreEffectsOptions,
        (obj) => 'folders?' + toQueryString(obj)
    );

    @Effect()
    ActionSuccess$ = CoreStore.Effects.ActionSuccess(this._coreEffectsOptions);

    @Effect()
    ActionFailure$ = CoreStore.Effects.ActionFailure(this._coreEffectsOptions);

    @Effect()
    SuccessReset$ = CoreStore.Effects.SuccessReset(this._coreEffectsOptions);

    @Effect()
    FlushOnLogout$ = CoreStore.Effects.FlushOnLogout(this._coreEffectsOptions);

    @Effect()
    SuccessReload$ = this.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.CreateSuccess,
            this._coreEffectsOptions.Actions.Types.UpdateSuccess,
            this._coreEffectsOptions.Actions.Types.DeleteSuccess),
        map((action: any) => action.payload),
        exhaustMap((folder) => of(new this._coreEffectsOptions.Actions.Load({ id: folder.parent.id })))
    );

    @Effect({dispatch: true})
    LoadSuccess$ = this.actions$.pipe(
        ofType(FeatureActions.Types.LoadSuccess),
        map((action: any) => action.payload),
        combineLatest(
            this.store.select(selectors.userFoldersLoaded)
        ),
        mergeMap((results) => {
            let folder = results[0];
            return _.compact([
                new FeatureActions.Set({ selected: _.omit(folder, ['children']) }),
                folder.isRoot && !folder.anotherUserFolder
                    ? new FeatureActions.Set({rootFolder: _.omit(folder, ['children']) })
                    : false,
                folder.isRoot && results[1] && folder.isMyFilesSection ? new FeatureActions.LoadUserFolders() : false
            ])
        })
    );

    @Effect()
    LoadSuccessSetRootFolder$ = this.actions$.pipe(
        ofType(FeatureActions.Types.LoadSuccess),
        map((action: any) => action.payload),
        exhaustMap((folder) =>
            of(new FeatureActions.Set({ selected: _.omit(folder, ['children']) }))
        )
    );

    //TODO: need better way of manipulating another Feature's store
    @Effect()
    StartUILoader$ = this.actions$.pipe(
        ofType(FeatureActions.Types.Load,
            FeatureActions.Types.Create,
            FeatureActions.Types.Delete,
            FeatureActions.Types.Update),
        exhaustMap(() => of({
            type: UILoaderStore.FeatureActions.Types.Set,
            payload: { isLoading: true }
        }))
    );

    @Effect()
    EndUILoader$ = this.actions$.pipe(
        ofType(
            FeatureActions.Types.Success,
            FeatureActions.Types.Failure
        ),
        exhaustMap(() => of({
            type: UILoaderStore.FeatureActions.Types.Set,
            payload: { isLoading: false }
        }))
    );

    constructor( protected actions$: Actions<any>,
                 protected _svc: Service,
                 protected store: Store<State>,
                 public toastrService: ToastrService) {
    }
}

export const selectors = {
    ...CoreStore.selectors(FEATURE_NAME),
    children: createSelector(selectState, (state: IState) => state.data.children),
    parent: createSelector(selectState, (state: IState) => _.omit(state.data, ['children'])),
    rootFolder: createSelector(selectState, (state: IState) => state.rootFolder),
    userFoldersLoaded: createSelector(selectState, (state: IState) => state.userFoldersLoaded)
};

@Injectable()
export class State extends BaseStateV2 {

    FEATURE_NAME: string = FEATURE_NAME;
    FeatureActions: IFeatureActions = FeatureActions;
    _selectors = selectors;

    private _ownerId: string;

    constructor(_store: Store<rootState>) {
        super(_store);
        this._store.pipe(
            select(userProfile),
            take(1)
        ).toPromise().then(p =>this._ownerId = p && p.id)
    }

    get rootFolder() {
        return this._store
            .select(selectors.rootFolder)
            .pipe(filter(root => root))
    }

    get rootPath() {
        return '/' + this._ownerId + '/'
    }

    isRoot(path: string): boolean {
        return this.rootPath === path
    }

    async load(fileItem: any = { path: this.rootPath }){

        var queryObj = this.getQueryObject(fileItem);

        return super.load(queryObj)
    }

    async loadMyFiles(fileItem: any = { path: this.rootPath }){

        var queryObj = this.getQueryObject(fileItem);
        queryObj.loadMyFiles = true;

        return super.load(queryObj)
    }

    async loadRecentFiles(fileItem: any = { path: this.rootPath }){

        var queryObj = this.getQueryObject(fileItem);
        queryObj.loadRecentFiles = true;

        return super.load(queryObj);
    }

    async loadBookMarks(fileItem: any = { path: this.rootPath }){

        var queryObj = this.getQueryObject(fileItem);
        queryObj.loadBookmarks = true;

        return super.load(queryObj);
    }

    async loadFoldersSharedWithMe(fileItem: any = { path: this.rootPath }){

        var queryObj = this.getQueryObject(fileItem);
        queryObj.loadFoldersSharedWithMe = true;

        return super.load(queryObj);

    }

    async loadFoldersSharedWithOthers(fileItem: any = { path: this.rootPath }){

        var queryObj = this.getQueryObject(fileItem);
        queryObj.loadFoldersSharedWithOthers = true;

        return super.load(queryObj);
    }

    getQueryObject(fileItem: any) {
        let queryObj: any = { };
        fileItem.id
            ? queryObj.id   = fileItem.id
            : queryObj.path = fileItem.path;

        if(fileItem.anotherUserFolder)
            queryObj.anotherUserFolder = fileItem.anotherUserFolder;

        if(fileItem.sharedWithMe)
            queryObj.realOwner = fileItem.owner;

        return queryObj;
    }

    resolveIcons(files) {
        return _.map(files, (fileItem) => {
            if(fileItem && fileItem.type === 'file')
                fileItem.icon = resolveIcon(fileItem.contentType);
            return fileItem
        })
    }

    get parent(): Observable<any> {
        return this._store.pipe(
            select(selectors.parent)
        )
    }

    get children(): Observable<any> {
        return this._store.pipe(
            select(selectors.children),
            map(this.resolveIcons)
        )
    }

    findChildFolder(attributes) {
        return this.children.pipe(
            map(children => _.filter(children, (child) => _.isMatch(child, attributes)))
        )
    }

    /*get children(): Observable<any> {
        return combineLatest(
            this._store.pipe(select(selectors.children)),
            //this._store.pipe(select(ShareStore.selectors.data))
        ).pipe(
            map(data => _.flatten(data)),
            map(this.resolveIcons.bind(this))
        )
    }*/

    get sorted() {
        return this.children.pipe(
            map(folders => {
                return _.sortBy(folders, [function(o: any) { return o && o.type; }])
            })
        )
    }

    async remove(obj) {
        return super.remove({ id: obj.id })
    }

    async loadAllUserFolders(){

        this._store.dispatch({
            type: FeatureActionTypes.LoadUserFolders,
        });
        return true
    }

    async unloadAllUserFolders(){
        this._store.dispatch({
            type: FeatureActionTypes.UnLoadUserFolders,
        });
        this._store.dispatch({
            type: FeatureActions.Types.Set,
            payload: {userFoldersLoaded: false}
        });
    }

}
